import React, {useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {parseIsoDate} from '@/entities/attendance/period';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {StatusDot} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme, withAlpha} from '@/shared/ui/theme';
import {formatCount, formatDayOfMonth} from '@/shared/lib/format';
import type {DailyTotals} from '../lib/aggregate';

/**
 * Attendance across the period, one stacked column per day: on-time below, late above,
 * absent on top — the same top-to-bottom "worse is higher" order the pass trend on the
 * dashboard uses, so the two charts read alike.
 *
 * Plain Views, no charting library — the same call `PassTrend` documents: a native
 * charting module would break the "the build always compiles for every target" rule,
 * and a bar chart is four styles.
 *
 * What this one adds over PassTrend is that a report period is not always a tidy seven
 * days. A month is 31 columns and a quarter is 90, so the plot SCROLLS horizontally
 * with a minimum column width instead of dividing the available space by n — at 90
 * columns in a phone's width, each bar would be 3px and the labels would overlap into
 * a grey smear. Scrolling keeps every day readable at the cost of a gesture, which is
 * the right trade for data nobody reads at a glance anyway.
 *
 * ## What was missing, and why it mattered
 *
 * The chart used to be bars with a legend and nothing else: no scale, no dates, no way
 * to find out what any single column was. A reader could see that Tuesday was worse
 * than Monday and could not learn by how much, which turns the one picture on the
 * screen into decoration. Three things fix it, none of them a dependency:
 *
 *   - A SCALE. The tallest column is labelled, and a hairline is drawn at it, so a bar
 *     has a size and not just a height relative to its neighbours.
 *   - A READOUT. Tapping a column names the day and prints its three numbers above the
 *     plot. This is the tooltip a touch screen can actually have.
 *   - WEEKENDS, marked. A dip on Saturday is not a problem, and a chart that draws it
 *     identically to a dip on Wednesday invites a manager to go asking about it.
 */
const CHART_HEIGHT = 132;
const MIN_SEGMENT = 3;
const MIN_COLUMN = 30;
const AXIS_WIDTH = 26;

const styles = StyleSheet.create({
  head: {gap: spacing(2), marginBottom: spacing(3)},
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(3),
    flexWrap: 'wrap',
  },
  legend: {flexDirection: 'row', alignItems: 'center', gap: spacing(3), flexWrap: 'wrap'},
  legendItem: {flexDirection: 'row', alignItems: 'center', gap: spacing(1)},

  readout: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(1),
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(2),
    borderRadius: radii.md,
    minHeight: 36,
  },
  readoutFacts: {flexDirection: 'row', alignItems: 'center', gap: spacing(3), flexWrap: 'wrap'},
  fact: {flexDirection: 'row', alignItems: 'center', gap: spacing(1)},
  factDot: {width: 7, height: 7, borderRadius: 4},

  frame: {flexDirection: 'row', gap: spacing(2)},
  scroll: {flex: 1},
  axis: {width: AXIS_WIDTH, height: CHART_HEIGHT, justifyContent: 'space-between'},
  axisLabel: {textAlign: 'right'},
  plot: {gap: spacing(2)},
  row: {flexDirection: 'row', alignItems: 'flex-end', height: CHART_HEIGHT, gap: 4},
  gridline: {position: 'absolute', left: 0, right: 0, top: 0, height: StyleSheet.hairlineWidth},
  col: {
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: MIN_COLUMN,
    height: CHART_HEIGHT,
    borderRadius: radii.xs,
  },
  bar: {
    width: '68%',
    maxWidth: 22,
    minWidth: 8,
    borderTopLeftRadius: radii.xs,
    borderTopRightRadius: radii.xs,
    overflow: 'hidden',
    justifyContent: 'flex-start',
  },
  seg: {width: '100%'},
  baseline: {height: StyleSheet.hairlineWidth},
  labels: {flexDirection: 'row', gap: 4},
  label: {minWidth: MIN_COLUMN, alignItems: 'center', paddingVertical: 2, borderRadius: radii.xs},
  empty: {height: CHART_HEIGHT, alignItems: 'center', justifyContent: 'center'},
});

/** "YYYY-MM-DD" → 1 (Mon) … 7 (Sun), the order the weekday labels are keyed by. */
function weekdayIndex(date: string): number {
  const day = parseIsoDate(date).getDay();
  return day === 0 ? 7 : day;
}

/** "2026-08-12" → "12.08" — a column label that survives a period spanning months. */
function shortDate(date: string): string {
  return `${date.slice(8, 10)}.${date.slice(5, 7)}`;
}

export function DailyChart({
  days,
  /**
   * Offered only where the day rows were fetched on request (the company report):
   * turning them on is a heavier query, so the way back has to exist and be visible.
   */
  onHide,
}: {
  days: DailyTotals[];
  onHide?: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  // Which column the reader asked about. A touch screen has no hover, so the "tooltip"
  // is a persistent readout above the plot and the selection is a tap that toggles.
  const [selected, setSelected] = useState<string | null>(null);

  const totals = days.map(d => d.onTime + d.late + d.absent || d.present);
  const max = Math.max(1, ...totals);
  const anything = totals.some(v => v > 0);
  const current = days.find(d => d.date === selected) ?? null;

  const fact = (color: string, label: string, value: number) => (
    <View style={styles.fact} key={label}>
      <View style={[styles.factDot, {backgroundColor: color}]} />
      <AppText variant="micro" color={p.foreground}>
        {label}
      </AppText>
      <AppText variant="mono">{formatCount(value)}</AppText>
    </View>
  );

  const plot = (
    <View style={styles.plot}>
      <View style={styles.row}>
        {/* The scale line: without it a bar's height is only a comparison with the bar
            beside it, and "how many people is that" has no answer on the chart. */}
        <View style={[styles.gridline, {backgroundColor: p.border}]} />

        {days.map(day => {
          // `present` is the fallback height for a scope with no schedules at all,
          // where every day is `unscheduled` and none of the three verdict counters
          // ever fires — without it the whole chart would be flat at zero for a
          // company that is plainly at work.
          const total = day.onTime + day.late + day.absent || day.present;
          const height = (total / max) * (CHART_HEIGHT - 10);
          const seg = (count: number) =>
            count > 0 ? Math.max((count / total) * height, MIN_SEGMENT) : 0;

          const neutral = day.onTime + day.late + day.absent === 0 ? day.present : 0;
          const weekend = weekdayIndex(day.date) > 5;
          const active = day.date === selected;

          return (
            <Pressable
              key={day.date}
              accessibilityRole="button"
              accessibilityState={{selected: active}}
              accessibilityLabel={`${shortDate(day.date)}: ${formatCount(total)}`}
              onPress={() => setSelected(active ? null : day.date)}
              testID={`chart-day-${day.date}`}
              style={[
                styles.col,
                {
                  backgroundColor: active
                    ? p.accent
                    : weekend
                    ? withAlpha(p.mutedForeground, 0.07)
                    : 'transparent',
                },
              ]}>
              <View
                style={[
                  styles.bar,
                  {
                    height: Math.max(height, total > 0 ? MIN_SEGMENT : 3),
                    backgroundColor: total > 0 ? 'transparent' : p.muted,
                  },
                ]}>
                {day.absent > 0 ? (
                  <View
                    style={[
                      styles.seg,
                      {height: seg(day.absent), backgroundColor: p.destructive},
                    ]}
                  />
                ) : null}
                {day.late > 0 ? (
                  <View
                    style={[
                      styles.seg,
                      {height: seg(day.late), backgroundColor: p.warning},
                    ]}
                  />
                ) : null}
                {day.onTime > 0 ? (
                  <View
                    style={[
                      styles.seg,
                      {height: seg(day.onTime), backgroundColor: p.signal},
                    ]}
                  />
                ) : null}
                {neutral > 0 ? (
                  <View
                    style={[
                      styles.seg,
                      {height: Math.max(height, MIN_SEGMENT), backgroundColor: p.signal},
                    ]}
                  />
                ) : null}
              </View>
            </Pressable>
          );
        })}
      </View>

      <View style={[styles.baseline, {backgroundColor: p.border}]} />

      <View style={styles.labels}>
        {days.map(day => {
          const active = day.date === selected;
          return (
            <View
              key={day.date}
              style={[styles.label, active && {backgroundColor: p.accent}]}>
              <AppText
                variant="micro"
                color={
                  active
                    ? p.accentForeground
                    : weekdayIndex(day.date) > 5
                    ? withAlpha(p.mutedForeground, 0.7)
                    : undefined
                }>
                {formatDayOfMonth(day.date)}
              </AppText>
            </View>
          );
        })}
      </View>
    </View>
  );

  return (
    <Card padding={4} testID="daily-chart">
      <View style={styles.head}>
        <View style={styles.titleRow}>
          <AppText variant="heading">{t('reports.dailyChart')}</AppText>
          <View style={styles.legend}>
            <View style={styles.legendItem}>
              <StatusDot tone="granted" size={6} />
              <AppText variant="micro">{t('attendance.flag.onTime')}</AppText>
            </View>
            <View style={styles.legendItem}>
              <StatusDot tone="pending" size={6} />
              <AppText variant="micro">{t('attendance.summary.late')}</AppText>
            </View>
            <View style={styles.legendItem}>
              <StatusDot tone="denied" size={6} />
              <AppText variant="micro">{t('attendance.summary.absent')}</AppText>
            </View>
            {onHide ? (
              <AppButton
                title={t('reports.hideChart')}
                tone="ghost"
                size="sm"
                onPress={onHide}
                testID="chart-hide"
              />
            ) : null}
          </View>
        </View>

        {/* The readout doubles as the instruction: before anything is picked it says
            what picking does, so the affordance never has to be guessed. */}
        <View
          testID="chart-readout"
          style={[
            styles.readout,
            {backgroundColor: current ? p.accent : p.muted},
          ]}>
          {current ? (
            <View style={styles.readoutFacts}>
              <AppText variant="subtitle" color={p.accentForeground}>
                {t(`attendance.weekday.${weekdayIndex(current.date)}`)},{' '}
                {shortDate(current.date)}
              </AppText>
              {fact(p.signal, t('attendance.flag.onTime'), current.onTime)}
              {fact(p.warning, t('attendance.summary.late'), current.late)}
              {fact(p.destructive, t('attendance.summary.absent'), current.absent)}
            </View>
          ) : (
            <AppText variant="caption">{t('reports.dailyChartHint')}</AppText>
          )}
        </View>
      </View>

      {days.length === 0 || !anything ? (
        <View style={styles.empty}>
          <AppText variant="body" color={p.mutedForeground}>
            {t('reports.noData')}
          </AppText>
        </View>
      ) : (
        <View style={styles.frame}>
          {/* A two-mark axis, not a full set of gridlines: the question a reader has of
              this chart is "how many people", and the top and the floor answer it. */}
          <View style={styles.axis}>
            <AppText variant="micro" style={styles.axisLabel}>
              {formatCount(max)}
            </AppText>
            <AppText variant="micro" style={styles.axisLabel}>
              0
            </AppText>
          </View>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.scroll}>
            {plot}
          </ScrollView>
        </View>
      )}
    </Card>
  );
}
